<template>
  <FullPage v-hotkey="shortcuts" class="workspace-home">
    <!-- The personal organization has no organization screen to navigate
         to, so its workspace shows no organization crumb. -->
    <template v-if="orgName && !isPersonalOrg" #topbar-left>
      <button
        v-tippy="{
          content: orgName,
          arrow: false,
          animation: 'fade',
          duration: 200,
        }"
        class="org-icon"
        type="button"
        :aria-label="`Open ${orgName}`"
        @click="openOrganization($event)"
      >
        <OrganizationIcon />
      </button>
    </template>
    <template #topbar-right>
      <BaseButton
        v-if="orgId"
        kind="auxiliary"
        :text="isPinned ? 'Unpin' : 'Pin'"
        @click="togglePin"
      >
        <template #icon>
          <PinIcon :class="{ pinned: isPinned }" />
        </template>
      </BaseButton>
      <BaseButton
        kind="auxiliary"
        text="Settings"
        @click="goToWorkspaceSettings"
      >
        <template #icon>
          <SettingsIcon />
        </template>
      </BaseButton>
      <ButtonMenu :offset="[0, 4]" placement="bottom-end">
        <template #btn>
          <BaseButton kind="auxiliary">
            <template #icon>
              <MoreIcon />
            </template>
          </BaseButton>
        </template>
        <template #content>
          <BaseButton
            class="context-action"
            kind="auxiliary"
            text="Copy link"
            is-expanded
            @click="copyLink"
          >
            <template #icon>
              <CopyLinkIcon />
            </template>
            <template #right>{{ copyLinkShortcutDisplay }}</template>
          </BaseButton>
        </template>
      </ButtonMenu>
    </template>
    <div class="content">
      <div class="workspace-title-block">
        <h2>{{ workspace?.name || "Workspace" }}</h2>
        <!-- The personal organization has a single member: its members row
             is pointless, so hide it. -->
        <template v-if="canShowWorkspace && !isPersonalOrg">
          <div v-if="memberEntries.length > 0" class="members-row">
            <ul class="members-avatars">
              <li
                v-for="[memberId, member] in memberEntries"
                :key="memberId"
                v-tippy="{
                  content: member.name || member.email,
                  placement: 'top',
                  animation: 'fade',
                  duration: 200,
                }"
                class="members-avatars__item"
                @click="openMember(memberId, $event)"
              >
                <Avatar
                  :personish="{
                    kind: 'attendee',
                    email: member.email,
                    name: member.name,
                  }"
                  size="small"
                  badge-side="hide"
                />
              </li>
            </ul>
            <button
              v-if="canAddMember"
              v-tippy="{
                content: 'Add member',
                placement: 'top',
                animation: 'fade',
                duration: 200,
              }"
              class="invite-btn"
              @click="goToWorkspaceSettings"
            >
              <PlusIcon />
            </button>
          </div>
          <p v-else class="members-empty">No members yet.</p>
        </template>
      </div>

      <template v-if="canShowWorkspace">
        <section v-if="workspace?.home" class="objects-section">
          <div class="section-heading-with-icon">
            <h3>Objects</h3>
          </div>
          <ObjectHierarchyTree
            :workspace-id="workspaceId"
            :root-object-id="workspace.home"
          />
        </section>

        <section class="databases-section">
          <div class="section-heading-with-icon databases-heading">
            <h3>Databases</h3>
            <BaseButton
              v-if="canCreateDatabase"
              kind="main"
              type="primary"
              text="New"
              @click="goToNewType"
            />
          </div>
          <div class="databases-block">
            <div v-if="databaseListItems.length > 0" class="table-list">
              <div class="column-headers">
                <span class="col-object">Database</span>
                <span class="col-nature">Nature</span>
                <span class="col-objects">Objects</span>
                <span class="col-properties">Properties</span>
                <span class="col-edit"></span>
              </div>
              <div
                v-for="db in databaseListItems"
                :key="db.id"
                class="table-row"
                @click="goToObjects(db)"
              >
                <div class="table-cell with-icon col-object">
                  <BaseType
                    :title="db.dataName"
                    :image="db.defaultIcon"
                    :color="db.color"
                    state="img"
                    size="s"
                    :clickable="false"
                  />
                  <span>{{ db.dataName }}</span>
                </div>
                <div class="table-cell col-nature">
                  <span class="badge" :class="db.nature">{{ db.nature }}</span>
                </div>
                <div class="table-cell secondary col-objects">
                  {{ objectCounts[db.id] ?? "\u2014" }}
                </div>
                <div class="table-cell secondary col-properties">
                  {{ db.propertyCount }}
                </div>
                <div class="table-cell col-edit">
                  <BaseButton
                    kind="auxiliary"
                    type="secondary"
                    text="Edit"
                    class="edit-btn"
                    @click.stop="goToConfig(db)"
                  />
                  <BaseButton
                    kind="auxiliary"
                    :tooltip="{ text: 'Database' }"
                    tooltip-display="always"
                    class="database-icon-button"
                    @click.stop="goToObjects(db)"
                  >
                    <template #icon>
                      <DatabaseIcon />
                    </template>
                  </BaseButton>
                </div>
              </div>
            </div>
            <div v-else class="empty-types">No types yet.</div>
          </div>
        </section>
      </template>
      <div v-else class="empty">Workspace not found.</div>
    </div>
  </FullPage>
</template>

<script lang="ts">
import type { Component } from "vue";
import { computed, defineComponent, ref, toRefs, watch } from "vue";

import type {
  CalendarColor,
  FullWorkspace,
  GraphDatabase,
  WorkspaceMember,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseType from "@/components/base/BaseType.vue";
import {
  getTypeColor,
  getTypeIcon,
  isBuiltinDatabase,
} from "@/components/databases";
import Avatar from "@/components/elements/Avatar.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import { graphRecordToProperties } from "@/components/graph/component";
import { getGraphQuery, runCompiledQuery } from "@/components/graph/query";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import MoreIcon from "@/components/icons/MoreIcon.vue";
import OrganizationIcon from "@/components/icons/OrganizationIcon.vue";
import PinIcon from "@/components/icons/PinIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import SettingsIcon from "@/components/icons/tabs/SettingsIcon.vue";
import * as inject from "@/components/inject";
import ObjectHierarchyTree from "@/components/ObjectHierarchyTree.vue";
import {
  assignShortcuts,
  copyLinkItem,
  type Shortcuts,
} from "@/components/shortcuts";
import { MEMBERS_TABLE_ID } from "@/components/tables";
import FullPage from "@/components/templates/FullPage.vue";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";
import {
  navigateTo,
  navigateToCustomObject,
  navigateToDatabase,
  navigateToType,
} from "@/utils/navigate";

type DatabaseListItem = {
  id: string;
  name: string;
  dataName: string;
  workspaceId: string;
  defaultIcon: Component | string;
  color: CalendarColor;
  nature: "default" | "custom";
  propertyCount: number;
};

export default defineComponent({
  name: "WorkspaceHome",
  components: {
    Avatar,
    BaseButton,
    BaseType,
    ButtonMenu,
    CopyLinkIcon,
    DatabaseIcon,
    FullPage,
    MoreIcon,
    ObjectHierarchyTree,
    OrganizationIcon,
    PinIcon,
    PlusIcon,
    SettingsIcon,
  },
  props: {
    orgId: {
      type: String,
      required: true,
    },
    workspaceId: {
      type: String,
      required: true,
    },
  },
  async setup(props) {
    const { orgId, workspaceId } = toRefs(props);
    const controller = inject.controller();
    const menu = inject.menu();
    const router = inject.router();
    const profile = inject.profile();
    const isModalShown = inject.isModalShown();
    const isInputFocused = inject.isInputFocused();

    const releaser = useReleaser();

    const org = orgId.value
      ? (await controller.organization.get(orgId.value)).release()
      : null;
    const orgName = org?.name ?? null;

    const openOrganization = (event?: MouseEvent) => {
      if (!orgId.value) return;
      void navigateTo(
        RouteNames.Organization,
        router,
        { params: { orgId: orgId.value } },
        event
      );
    };

    const workspaceP = bindAsync(workspaceId, async (id) => {
      if (!id) return releasableRef<FullWorkspace | null>(null);
      const r = await controller.workspace.get(id);
      const v = r.value;
      r.release();
      return releasableRef(v);
    });
    releaser.add(workspaceP);

    const allDatabasesP = controller.graph.database.list();
    releaser.add(allDatabasesP);

    const [workspace, allDatabases] = await Promise.all([
      workspaceP,
      allDatabasesP,
    ]);

    const canShowWorkspace = computed(
      () => workspace.value != null && workspace.value.owner === orgId.value
    );

    // The personal organization is not navigable and has a single member:
    // the template hides its organization crumb and members row.
    const isPersonalOrg = computed(
      () => orgId.value === profile.value.user.personal.organizationId
    );

    const memberEntries = computed((): Array<[string, WorkspaceMember]> => {
      const m = workspace.value?.members;
      if (!m) return [];
      return Object.entries(m).toSorted((a, b) =>
        a[1].name.localeCompare(b[1].name)
      );
    });

    const getPropertyCount = (database: GraphDatabase): number => {
      if (!database.type) return 0;
      return graphRecordToProperties(database.type).length;
    };

    const databaseListItems = computed<DatabaseListItem[]>(() => {
      const wsId = workspaceId.value;
      const all = allDatabases.value;
      if (!wsId || !all) return [];

      const dict = all[wsId];
      if (!dict) return [];

      const raw = Object.values(dict);
      const sorted = raw.toSorted((a, b) => {
        const aIsDefault = isBuiltinDatabase(a);
        const bIsDefault = isBuiltinDatabase(b);
        if (aIsDefault && !bIsDefault) return -1;
        if (!aIsDefault && bIsDefault) return 1;
        return a.data_name.localeCompare(b.data_name);
      });

      return sorted.map((db) => {
        const isDefault = isBuiltinDatabase(db);
        return {
          id: db.id,
          name: db.name,
          dataName: db.data_name,
          workspaceId: wsId,
          defaultIcon: getTypeIcon(profile.value, db),
          color: getTypeColor(db),
          nature: isDefault ? "default" : "custom",
          propertyCount: getPropertyCount(db),
        };
      });
    });

    const getObjectCount = async (database: GraphDatabase): Promise<number> => {
      const query = await getGraphQuery(
        database.workspace,
        `length(${database.name})`,
        controller,
        { databaseName: { type: "const", value: database.name } }
      );
      if (!query) return 0;
      const result = await runCompiledQuery(query, controller);
      const count = typeof result.value === "bigint" ? result.value : 0;
      result.release();
      return Number(count);
    };

    const objectCounts = ref<Record<string, number>>({});

    releaser.defer(
      watch(
        [databaseListItems, allDatabases],
        async ([items, all]) => {
          if (!items?.length || !all) return;
          const wsId = workspaceId.value;
          if (!wsId) return;
          const newCounts: Record<string, number> = {};
          const promises: Promise<void>[] = [];
          const wsDict = all[wsId];

          items.forEach((item) => {
            const db = wsDict?.[item.id];
            if (db) {
              promises.push(
                getObjectCount(db).then((count) => {
                  newCounts[item.id] = count;
                })
              );
            }
          });

          await Promise.all(promises);
          objectCounts.value = newCounts;
        },
        { immediate: true }
      )
    );

    const goToObjects = (db: DatabaseListItem) => {
      navigateToDatabase(router, db.workspaceId, db.id);
    };

    const goToConfig = (db: DatabaseListItem) => {
      navigateToType(router, orgId.value, db.workspaceId, db.id);
    };

    // A `reader` member of the workspace cannot create a database (a schema
    // write): the "New" button is hidden.
    const canCreateDatabase = computed(() =>
      isWorkspaceWritable(profile.value, workspaceId.value)
    );
    const goToNewType = () => {
      navigateToType(router, orgId.value, workspaceId.value);
    };

    const canAddMember = computed(
      () => workspace.value?.permissions?.includes("member-add") ?? false
    );

    // Member objects live in the workspace's members database, keyed by the
    // member's account id.
    const openMember = (memberId: string, event?: MouseEvent) =>
      navigateToCustomObject(
        router,
        workspaceId.value,
        MEMBERS_TABLE_ID,
        memberId,
        event
      );

    const goToWorkspaceSettings = () => {
      router.push({
        name: RouteNames.SettingsWorkspace,
        params: { orgId: orgId.value, workspaceId: workspaceId.value },
      });
    };

    const isPinned = computed(() => menu.isItemPinned(workspaceId.value));

    const togglePin = async () => {
      await menu.toggleWorkspaceHomepagePin(workspaceId.value);
    };

    const copyLink = async () => {
      const url = `https://open.routine.co?path=/workspaces/${orgId.value}/${workspaceId.value}`;
      await copy(url);
      controller.notifications.notify("Link copied to clipboard");
    };

    const shortcuts = computed<Shortcuts>(() => ({
      ...assignShortcuts({ isModalShown, isInputFocused }, copyLinkItem, () => {
        void copyLink();
      }),
    }));

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    return {
      workspace,
      canAddMember,
      canShowWorkspace,
      copyLinkShortcutDisplay,
      isPersonalOrg,
      goToWorkspaceSettings,
      orgName,
      openOrganization,
      openMember,
      memberEntries,
      databaseListItems,
      objectCounts,
      copyLink,
      shortcuts,
      isPinned,
      togglePin,
      goToObjects,
      goToConfig,
      goToNewType,
      canCreateDatabase,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/context-actions.scss";
@use "../styles/settings.scss";

.org-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  background: transparent;
  border: none;
  cursor: default;

  svg {
    width: 14px;
    height: 14px;
  }

  @include themed() {
    svg .fill {
      fill: t($grey-50);
    }
  }

  &:hover {
    @include themed() {
      background: t($grey-10);

      svg .fill {
        fill: t($grey-70);
      }
    }
  }
}

.workspace-home {
  .content {
    overflow-y: auto;
    height: 100%;
    width: 100%;
    padding-bottom: 96px;
    max-width: $document-wide-max-width;
    margin: 0 auto;
  }

  .workspace-title-block {
    margin-bottom: 24px;

    h2 {
      text-align: center;
      margin: 0 0 12px;
    }
  }
}

.section-heading-with-icon {
  display: flex;
  align-items: center;
  column-gap: 10px;
  margin-bottom: 12px;

  h3 {
    margin-bottom: 0;
  }

  &.databases-heading {
    justify-content: space-between;
  }
}

.section-heading-icon {
  flex-shrink: 0;
}

.members-avatars {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.members-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.members-avatars__item {
  flex-shrink: 0;
  @include transition(opacity 150ms ease);

  &:hover {
    opacity: 0.7;
  }
}

.invite-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  flex-shrink: 0;
  cursor: default;
  @include transition(all 150ms ease);

  @include themed() {
    background: t($primary);
    color: t($bg);
  }

  :deep(svg) {
    width: 10px;
    height: 10px;

    .stroke {
      stroke: currentColor;
    }
  }

  &:hover {
    opacity: 0.7;
  }
}

.members-empty {
  @extend %font-body-regular;
  margin: 0;
  text-align: center;

  @include themed() {
    color: t($grey-50);
  }
}

.objects-section {
  margin-bottom: 24px;
}

.databases-section {
  margin-bottom: 24px;
}

.databases-block {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
}

.databases-section .table-list {
  margin: 0 0 8px 0;

  .col-object {
    flex: 1;
    min-width: 160px;
  }

  .col-nature {
    width: 100px;
  }

  .col-objects {
    width: 80px;
  }

  .col-properties {
    width: 90px;
  }

  .col-edit {
    width: 120px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 8px;
  }

  .table-row {
    border-radius: $border-radius-m;
    padding: 10px 8px;

    :deep(button.base-button.edit-button:hover) {
      @include themed() {
        background-color: t($grey-15);
      }
    }

    &:hover {
      @include themed() {
        background: t($grey-03);
      }
    }
  }
}

.databases-section .database-icon-button {
  flex-shrink: 0;
}

.databases-section .badge {
  @extend %font-small-regular;
  font-weight: 500;
  letter-spacing: 0.02em;
  padding: 2px 8px;
  border-radius: $border-radius-m;
  text-transform: capitalize;

  &.default {
    @include themed() {
      border: 1px solid t($grey-15);
      color: t($grey-40);
    }
  }

  &.custom {
    @include themed() {
      border: 1px solid t($tertiary-20);
      color: t($tertiary-50);
    }
  }
}

.databases-section .empty-types {
  @extend %font-body-regular;
  padding: 8px 0;

  @include themed() {
    color: t($grey-60);
  }
}

.empty {
  @extend %font-body-regular;

  @include themed() {
    color: t($grey-60);
  }
}
</style>
